import wq_2021 from "@/assets/woonquotes/2021.json";
import wq_2022 from "@/assets/woonquotes/2022.json";
import wq_2023 from "@/assets/woonquotes/2023.json";
import wq_2024 from "@/assets/woonquotes/2024.json";
import wq_2025 from "@/assets/woonquotes/2025.json";
import wq_2026 from "@/assets/woonquotes/2026.json";

import type { ToetsHuishoudenParameters } from "@/models/ToetsHuishouden";
import { isKeyOf } from "./typeHelpers";

const woonquotes = {
	2021: wq_2021,
	2022: wq_2022,
	2023: wq_2023,
	2024: wq_2024,
	2025: wq_2025,
	2026: wq_2026,
};

interface WoonquoteIncomeRange {
	minIncome: number;
	maxIncome?: number;
	interestRanges: {
		minInterestRate: number;
		maxInterestRate?: number;
		value: number;
	}[];
}

function getMostRecentWoonquoteIncomeRange() {
	const years = Object.keys(woonquotes).map((year) => Number.parseInt(year));
	const mostRecentYear = Math.max(...years) as keyof typeof woonquotes;
	const mostRecentWoonquote = woonquotes[mostRecentYear];
	return mostRecentWoonquote;
}

function getWoonquoteIncomeRange(
	huishouden: ToetsHuishoudenParameters,
	jaar: number,
) {
	const aow = huishouden.aow ? "aow" : "regular";
	const box = huishouden.box1 ? "" : "-box3";

	const key = `${aow}${box}` as const;
	const woonquoteIncomeRanges: WoonquoteIncomeRange[] =
		isKeyOf(woonquotes, jaar) && isKeyOf(woonquotes[jaar], key)
			? woonquotes[jaar]?.[key]
			: getMostRecentWoonquoteIncomeRange()[key];

	return woonquoteIncomeRanges.find((quote) => {
		if (huishouden.toetsinkomen >= quote.minIncome) {
			if (quote.maxIncome) {
				return huishouden.toetsinkomen < quote.maxIncome;
			}
			return true;
		}
	});
}

function getWoonquoteCutoffs(
	huishouden: ToetsHuishoudenParameters,
	jaar: number,
	minPercentage: number,
	maxPercentage: number,
) {
	if (!Number.isFinite(huishouden.toetsinkomen)) {
		return undefined;
	}

	const woonquoteIncomeRange = getWoonquoteIncomeRange(huishouden, jaar);
	const possibleCutoffs = woonquoteIncomeRange?.interestRanges
		.map((r) => r.minInterestRate)
		.filter((r) => r >= minPercentage && r <= maxPercentage);

	return possibleCutoffs;
}

function getWoonquote(
	huishouden: ToetsHuishoudenParameters,
	toetsrente: number,
	jaar: number = new Date().getFullYear(),
): number | undefined {
	if (
		!Number.isFinite(huishouden.toetsinkomen) ||
		!Number.isFinite(toetsrente)
	) {
		return undefined;
	}

	const woonquoteIncomeRange = getWoonquoteIncomeRange(huishouden, jaar);
	const woonquote = woonquoteIncomeRange?.interestRanges.find((range) => {
		if (toetsrente >= range.minInterestRate) {
			if (range.maxInterestRate) {
				// We do this to prevent floating point rounding errors
				const diff = toetsrente * 1_000_000 - range.maxInterestRate * 1_000_000;
				return toetsrente <= range.maxInterestRate || diff < 10;
			}
			return true;
		}
	});

	return woonquote?.value;
}

export { getWoonquote, getWoonquoteCutoffs };
